import { useEffect } from 'react';
import { useDrop } from 'react-dnd';
import type { DragTypes } from './constants';
import { MoveStates } from './constants';
import type { TopicReorganizationTree } from './useTopicReorganization';
import isReorganizationPostEdgePinned from './utils/isReorganizationPostEdgePinned';
import type {
  ContentMapDropParams,
  ContentMapGetDropParams,
} from './utils/types';

const getTopicDropParams = (
  tree: TopicReorganizationTree,
  accept: DragTypes[],
  getDropParams: ContentMapGetDropParams,
): ContentMapDropParams => {
  const pinnedPosts =
    tree.reorganizationTopicPosts.status === 'loaded'
      ? tree.reorganizationTopicPosts.edges.filter(
          isReorganizationPostEdgePinned,
        )
      : [];

  return getDropParams(accept, {
    destinationTree: tree,
    indexWithPlaceholder: tree.children.length,
    pinnedPostIndexWithPlaceholder: pinnedPosts.length,
    canDrop: (item) =>
      // pinned post reordering should be handled by TopicTreeSeparator
      [...item.toMove.pinnedPostKeys, ...item.toMove.postKeys].every((key) => {
        const edge =
          tree.reorganizationTopicPosts.status === 'loaded' &&
          tree.reorganizationTopicPosts.edges.find((p) => p.postKey === key);

        return (
          !edge ||
          edge.reorganizationMetadata.move.state === MoveStates.movedOut
        );
      }),
  });
};

interface UseTopicDropArgs {
  tree: TopicReorganizationTree;
  topicId: string;
  getDropParams: ContentMapGetDropParams;
  onDragOverChange: (
    isDragOver: boolean,
    ops: { key: string; topicId: string },
  ) => void;
}

const useTopicDrop = (
  accept: DragTypes[],
  { tree, topicId, getDropParams, onDragOverChange }: UseTopicDropArgs,
) => {
  const [{ isOver: isDragOver }, dropRef] = useDrop(
    getTopicDropParams(tree, accept, getDropParams),
  );

  useEffect(() => {
    onDragOverChange(isDragOver, {
      key: topicId,
      topicId: tree.topic.id,
    });
  }, [onDragOverChange, isDragOver, topicId, tree]);

  return dropRef;
};

export default useTopicDrop;
